import React, { useState } from 'react';
import FieldRow from '../template-editor/FieldRow';
import './RegionList.css';

export default function RegionList({
  regions,
  fields,
  selectedRegion,
  selectedField,
  onSelectRegion,
  onSelectField,
  onAddField,
  getRegionColor,
  onFieldRegionChange,
  librarySelectedKeys = new Set(),
  zones = [],
  onToggleEnabled,
  onPlaceZone,
  onEditField,
  onDeleteField,
}) {
  const [expandedRegions, setExpandedRegions] = useState(
    regions.reduce((acc, r) => ({ ...acc, [r.id]: true }), {})
  );
  const [dragFieldId, setDragFieldId] = useState(null);
  const [dragOverRegionId, setDragOverRegionId] = useState(null); // null=none, '__unassigned__'=unassigned section, or region UUID

  function handleDragStart(e, fieldId) {
    setDragFieldId(fieldId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldId);
  }

  function handleDragEnd() {
    setDragFieldId(null);
    setDragOverRegionId(null);
  }

  function handleDragOver(e, regionId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverRegionId(regionId);
  }

  function handleDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverRegionId(null);
    }
  }

  async function handleDrop(e, targetRegionId) {
    e.preventDefault();
    setDragOverRegionId(null);
    const fieldId = e.dataTransfer.getData('text/plain') || dragFieldId;
    setDragFieldId(null);
    if (!fieldId || !onFieldRegionChange) return;
    const actualTarget = targetRegionId === '__unassigned__' ? null : targetRegionId;
    const field = fields.find(f => f.id === fieldId);
    const currentRegionId = field?.region_id || null;
    if (currentRegionId === actualTarget) return;
    await onFieldRegionChange(fieldId, actualTarget);
  }
  
  // Group fields by region
  const fieldsByRegion = fields.reduce((acc, field) => {
    const regionId = field.region_id || 'unassigned';
    if (!acc[regionId]) acc[regionId] = [];
    acc[regionId].push(field);
    return acc;
  }, {});
  
  function toggleRegion(regionId) {
    setExpandedRegions(prev => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  }
  
  // Sort regions by type order
  const regionOrder = ['header', 'parties', 'line_items', 'totals', 'footer', 'custom'];
  const sortedRegions = [...regions].sort((a, b) => 
    regionOrder.indexOf(a.region_type) - regionOrder.indexOf(b.region_type)
  );
  
  return (
    <div className="region-list">
      <div className="region-list-header">
        <h3>Template Regions</h3>
        <div className="region-stats">
          {regions.length} regions, {fields.length} fields
        </div>
      </div>
      
      <div className="regions">
        {sortedRegions.map(region => {
          const color = getRegionColor(region.region_type);
          const regionFields = fieldsByRegion[region.id] || [];
          const isExpanded = expandedRegions[region.id];
          const isSelected = selectedRegion?.id === region.id;
          
          return (
            <div 
              key={region.id} 
              className={`region-group ${isSelected ? 'selected' : ''} ${dragOverRegionId === region.id ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, region.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, region.id)}
            >
              <div 
                className="region-header"
                onClick={() => onSelectRegion(region)}
                style={{ borderLeftColor: color.border }}
              >
                <button 
                  className="expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRegion(region.id);
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
                
                <span className="region-icon">{color.icon}</span>
                <span className="region-label">{region.label}</span>
                
                <div className="region-meta">
                  <span className="field-count">{regionFields.length} fields</span>
                  {region.confidence != null && (
                    <span 
                      className="confidence-badge"
                      style={{ 
                        backgroundColor: region.confidence >= 0.9 ? '#22c55e' : 
                                        region.confidence >= 0.7 ? '#f59e0b' : '#ef4444'
                      }}
                    >
                      {Math.round(region.confidence * 100)}%
                    </span>
                  )}
                </div>
              </div>
              
              {isExpanded && (
                <div className="region-fields">
                  {regionFields.length === 0 ? (
                    <div className="no-fields">No fields in this region</div>
                  ) : (
                    regionFields.map(field => {
                      const isFieldSelected = selectedField?.id === field.id;
                      const isEnabled = field.enabled !== false;
                      const fieldZones = zones.filter(z => z.field_id === field.id);

                      return (
                        <div
                          key={field.id}
                          className={dragFieldId === field.id ? 'field-row-draggable dragging' : 'field-row-draggable'}
                          style={{ opacity: dragFieldId === field.id ? 0.4 : 1 }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, field.id)}
                          onDragEnd={handleDragEnd}
                        >
                          <FieldRow
                            field={field}
                            isSelected={isFieldSelected}
                            isEnabled={isEnabled}
                            fieldZones={fieldZones}
                            librarySelectedKeys={librarySelectedKeys}
                            onSelect={() => onSelectField(field)}
                            onToggleEnabled={onToggleEnabled}
                            onPlaceZone={onPlaceZone}
                            onEdit={onEditField}
                            onDelete={onDeleteField}
                            extraActions={onFieldRegionChange && (
                              <button
                                className="field-unassign-btn"
                                onClick={(e) => { e.stopPropagation(); onFieldRegionChange(field.id, null); }}
                                title="Remove from region"
                              >
                                ×
                              </button>
                            )}
                          />
                        </div>
                      );
                    })
                  )}
                  
                  <button 
                    className="add-field-btn"
                    onClick={() => onAddField(region.id)}
                  >
                    + Add Field
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Unassigned fields */}
        {fieldsByRegion.unassigned && fieldsByRegion.unassigned.length > 0 && (
          <div className={`region-group unassigned ${dragOverRegionId === '__unassigned__' ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, '__unassigned__')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, '__unassigned__')}
          >
            <div className="region-header unassigned-header">
              <span className="region-icon">⚠️</span>
              <span className="region-label">Unassigned Fields</span>
              <span className="field-count">
                {fieldsByRegion.unassigned.length}
              </span>
            </div>
            <div className="region-fields">
              {fieldsByRegion.unassigned.map(field => {
                const isFieldSelected = selectedField?.id === field.id;
                const isEnabled = field.enabled !== false;
                const fieldZones = zones.filter(z => z.field_id === field.id);

                return (
                  <div
                    key={field.id}
                    className={dragFieldId === field.id ? 'field-row-draggable dragging' : 'field-row-draggable'}
                    style={{ opacity: dragFieldId === field.id ? 0.4 : 1 }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, field.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <FieldRow
                      field={field}
                      isSelected={isFieldSelected}
                      isEnabled={isEnabled}
                      fieldZones={fieldZones}
                      librarySelectedKeys={librarySelectedKeys}
                      onSelect={() => onSelectField(field)}
                      onToggleEnabled={onToggleEnabled}
                      onPlaceZone={onPlaceZone}
                      onEdit={onEditField}
                      onDelete={onDeleteField}
                      extraActions={onFieldRegionChange && (
                        <select
                          className="field-region-select"
                          value=""
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            if (e.target.value) {
                              onFieldRegionChange(field.id, e.target.value);
                              e.target.value = '';
                            }
                          }}
                          title="Move to region"
                        >
                          <option value="">Move to...</option>
                          {sortedRegions.map(r => {
                            const color = getRegionColor(r.region_type);
                            return (
                              <option key={r.id} value={r.id}>
                                {color.icon} {r.label}
                              </option>
                            );
                          })}
                        </select>
                      )}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
